import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "../components/ui/dialog";
import { Field, Input } from "../components/ui/input";

/** Asks for a name (saving a building block or a look, renaming). */
export function NameDialog({
  open,
  onOpenChange,
  title,
  description,
  label = "Name",
  initial = "",
  confirmLabel = "Speichern",
  pending = false,
  maxLength = 120,
  onConfirm,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  label?: string;
  initial?: string;
  confirmLabel?: string;
  pending?: boolean;
  maxLength?: number;
  onConfirm: (name: string) => Promise<unknown> | undefined;
  children?: ReactNode;
}) {
  const [name, setName] = useState(initial);
  useEffect(() => {
    if (open) setName(initial);
  }, [open, initial]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const value = name.trim();
    if (!value) return;
    try {
      await onConfirm(value);
    } catch {
      // The caller shows what went wrong; the dialog stays open to try again.
      return;
    }
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={title} description={description}>
        <form onSubmit={(event) => void submit(event)} className="space-y-4">
          <Field label={label}>
            <Input
              autoFocus
              value={name}
              maxLength={maxLength}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
          {children}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={!name.trim() || pending}>
              {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
